import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import classnames from 'classnames';
import {
  Button,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledButtonDropdown,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import EmptyStateImage from './components/Image';
import ImporterIcon from '../../components/Importer/Icon';
import EmptyArchive from './Archive';
import UpgradePlanButton from '../../components/UpgradePlanButton';

import {
  isArchive,
  isFavorites,
  isTrash,
} from '../../helpers/App';
import {
  userCan,
  userIsAdmin,
} from '../../helpers/Auth';
import { eventDispatch } from '../../helpers/Events';
import { upgradeModalIfNeeded } from '../../helpers/UpgradePlan';

const EmptyCollection = ({
  profile,
  allCollections,
  myCollections,
}) => {
  const intl = useIntl();
  const showUpload = !allCollections && !myCollections && userCan('file.upload') && !isArchive() && !isTrash() && !isFavorites();

  return (
    <div
      className={classnames('content-wrapper', {
        'nothing-found-wrapper': !allCollections,
        'empty-state-wrapper row align-items-center justify-content-center': allCollections,
        'has-upload-area': showUpload,
        'nothing-found-archive content-wrapper': isArchive(),
      })}
    >
      {(allCollections && !isArchive()) && (
        <>
          <Col lg="5" xs="12">
            <h2>{intl.formatMessage({ defaultMessage: 'Create a Collection to get started!' })}</h2>
            <div className="mt-4">
              {intl.formatMessage({ defaultMessage: 'Collections help organize your files, just like folders.' })}
            </div>
            {(!isArchive() && userCan('collection.create')) && (
              <Button color="primary" className="mt-4" onClick={() => { eventDispatch('openAddCollectionModal'); }}>
                <i className="fas fa-plus" />
                {intl.formatMessage({ defaultMessage: 'New Collection' })}
              </Button>
            )}
          </Col>
          <EmptyStateImage imageUrl="/images/empty-pages/empty-all-collections-page.png" height="high" />
        </>
      )}
      {myCollections && (
        <>
          <i className="far fa-bookmark fa-3x" />
          <h4>{intl.formatMessage({ defaultMessage: 'My Collections' })}</h4>
          <p>{intl.formatMessage({ defaultMessage: 'A private area where you can create Collections of files, just for you.' })}</p>
          <div className="button-wrapper">
            {!isArchive() && (
              <Button color="primary" onClick={() => { eventDispatch('openAddCollectionModal'); }}>
                <i className="fas fa-plus" />
                {intl.formatMessage({ defaultMessage: 'New Collection' })}
              </Button>
            )}
          </div>
        </>
      )}
      {showUpload && (
        <>
          <div className="upload-wrapper">
            <i className="far fa-upload fa-3x" />
            <h4>{intl.formatMessage({ defaultMessage: 'Drag and drop files and folders here' })}</h4>
            <Button
              color="primary"
              onClick={() => { eventDispatch('triggerFileUpload'); }}
            >
              <i className="far fa-upload" />
              {intl.formatMessage({ defaultMessage: 'Upload' })}
            </Button>
          </div>
          <div className="more-wrapper">
            <p>{intl.formatMessage({ defaultMessage: 'More ways to add files' })}</p>
            <div className="more-buttons-wrapper">
              <UncontrolledButtonDropdown group={false} className="upload-button-dropdown">
                <DropdownToggle color="secondary" outline caret>
                  {profile.importers.map(importer => (
                    <ImporterIcon key={importer.id} importer={importer} />
                  ))}
                  <span>{intl.formatMessage({ defaultMessage: 'Import' })}</span>
                </DropdownToggle>
                <DropdownMenu>
                  {profile.importers.map(importer => (
                    <DropdownItem
                      key={importer.id}
                      className="btn btn-outline-secondary"
                      onClick={() => { eventDispatch('openImportModal', importer.id); }}
                    >
                      <ImporterIcon importer={importer} />
                      <span>{importer.name}</span>
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </UncontrolledButtonDropdown>
              {userIsAdmin() && (
                <Button
                  className="upload-pages-button"
                  color="secondary"
                  outline
                  tag={Link}
                  to="/admin/upload-pages"
                  onClick={(e) => upgradeModalIfNeeded('upload-pages', () => {}, e)}
                >
                  <div className="icon-gray">
                    <i className="far fa-arrow-up-from-bracket" />
                  </div>
                  {intl.formatMessage({ defaultMessage: 'Create a Guest upload' })}
                  <UpgradePlanButton
                    feature="upload-pages"
                  />
                </Button>
              )}
            </div>
          </div>
        </>
      )}
      {(!allCollections && !myCollections && (!userCan('file.upload') || isArchive() || isTrash())) && (
        <>
          {isArchive() ? (
            <EmptyArchive />
          ) : (
            <>
              {isTrash() ? (
                <h4>{intl.formatMessage({ defaultMessage: 'No files in trash' })}</h4>
              ) : (
                <h4>{intl.formatMessage({ defaultMessage: 'No files in this Collection' })}</h4>
              )}
            </>
          )}
        </>
      )}
      {(allCollections && isArchive()) && (
        <EmptyArchive />
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
});

EmptyCollection.propTypes = {
  profile: PropTypes.object.isRequired,
  allCollections: PropTypes.bool,
  myCollections: PropTypes.bool,
};

EmptyCollection.defaultProps = {
  allCollections: false,
  myCollections: false,
};

export default connect(mapStateToProps)(EmptyCollection);
